import React, { useState, useRef, forwardRef } from "react";

import * as Material from "@mui/material";
import { IconChevronDown } from "@tabler/icons-react";

import './index.scss';

/**
 *
 * @param {variant: "default" | "rounded" | "text" | "dashed"} param0
 * @param {color: "white" | "black" | "primary" | "error" | "warning" | "info" | "success" | "inverse" | "current" | "overlay" | "ai"} color `current` matches the active UI mode (dark button in dark mode, light in light); `inverse` is its opposite; `ai` is the upgrade card's surface (slate glass, soft AI glows) with white text in both themes
 * @param {size: "micro" | "small" | "medium" | "large"} size
 * @param {highlightColor: boolean | "white" | "black" | "primary" | "error" | "warning" | "info" | "success" | "inverse" | "overlay" | "current"} highlightColor paints the icon container; `true` defaults to "inverse"
 * @param {discreet: boolean} discreet renders the button with no background and neutral text/icon; on hover the background, text and icon all adopt the `color` prop
 * @param {dropdown: boolean} dropdown the button opens a Popper/Menu: a transparent groove cut through the button splits off a trailing chevron segment
 */

const Button = forwardRef(function Button({ children, text, onClick, disabled = false, className = "", variant = "default", loading = false, color = "primary", size = "medium", tooltip, endIcon, startIcon, icon = null, fullWidth = false, preventLoading = false, highlightColor = false, discreet = false, dropdown = false, ...props }, ref) {
  const [_loading, _setLoading] = useState(loading);
  const internalRef = useRef(null);
  const buttonRef = ref || internalRef;
  const [ripples, setRipples] = useState([]);

  const createRipple = (event) => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    const ripple = {
      id: Date.now(),
      x,
      y,
      size
    };

    setRipples(prev => [...prev, ripple]);

    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== ripple.id));
    }, 600);
  };

  async function _onClick(event) {
    if (!disabled && !_loading) {
      createRipple(event);
    }
    if (!onClick) return;

    if (!preventLoading) _setLoading(true);

    try {
      await onClick(event);
    } catch (error) {
      console.error(error);
    }

    if (!preventLoading) _setLoading(false);
  }

  // highlightColor: paints the icon container with a color. `true` -> "inverse",
  // or pass any color accepted by the `color` prop.
  const resolvedHighlight = highlightColor === true ? "inverse" : highlightColor;
  const highlightClasses = resolvedHighlight
    ? ` CustomBtn_icon-highlight CustomBtn_icon-highlight-${resolvedHighlight}`
    : "";
  const startIconClassName = `CustomBtn_button-icon CustomBtn_icon-start${highlightClasses}`;
  const endIconClassName = `CustomBtn_button-icon CustomBtn_icon-end${highlightClasses}`;
  const iconOnlyClassName = `CustomBtn_button-icon${highlightClasses}`;

  const button = (
      <button
        ref={buttonRef}
        disabled={disabled}
        className={`
          CustomBtn_platform-button-base
          ${className}
          CustomBtn_${variant}
          ${icon ? "CustomBtn_icon" : ""}
          CustomBtn_${color}
          CustomBtn_${size}
          ${startIcon ? "CustomBtn_has-start-icon" : ""}
          ${endIcon ? "CustomBtn_has-end-icon" : ""}
          ${resolvedHighlight ? "CustomBtn_has-highlight-icon" : ""}
          ${discreet ? "CustomBtn_discreet" : ""}
          ${dropdown && !icon ? "CustomBtn_has-dropdown" : ""}
          ${fullWidth ? "CustomBtn_full-width" : ""}
          ${disabled ? "CustomBtn_disabled" : ""}
          ${_loading || loading ? "CustomBtn_loading" : ""}
        `}
        style={{
          width: fullWidth ? "100%" : "auto"
        }}
        onClick={_onClick}
        variant="contained"
        {...props}
      >
        {ripples.map(ripple => (
          <span
            key={ripple.id}
            className="CustomBtn_wave-effect"
            style={{
              left: `${ripple.x}px`,
              top: `${ripple.y}px`,
              width: `${ripple.size}px`,
              height: `${ripple.size}px`
            }}
          />
        ))}
        {startIcon && (
          <div className={startIconClassName}>
            {(_loading || loading)
              ? <Material.CircularProgress className="CustomBtn_loader" />
              : startIcon}
          </div>
        )}
        {icon && (
          <div className={iconOnlyClassName}>
            {(_loading || loading)
              ? <Material.CircularProgress className="CustomBtn_loader" />
              : icon}
          </div>
        )}
        {(children || text) && !icon && (
          <span className="CustomBtn_button-text">{children || text}</span>
        )}
        {endIcon && !(_loading || loading) && (
          <div className={endIconClassName}>
            {endIcon}
          </div>
        )}
        {dropdown && !icon && (
          <span className="CustomBtn_dropdown" aria-hidden="true">
            <IconChevronDown />
          </span>
        )}
      </button>
  );

  // No tooltip → no wrapper. With one, a disabled button needs an inline
  // wrapper: a disabled element fires no events, so MUI's Tooltip could not
  // open and logs an error in development.
  if (!tooltip) return button;

  return (
    <Material.Tooltip arrow title={tooltip}>
      {disabled ? <span className="CustomBtn_tooltip-anchor">{button}</span> : button}
    </Material.Tooltip>
  )
});

export default Button;
